import React, { useState, useEffect } from "react";
import { Modal }              from "react-bootstrap";
import { toast }              from "react-toastify";
import CloseBtn               from "assets/icons/Close-1.svg";
import PasscodeInput          from "components/PasscodeInput/PasscodeInput";
import WalletPasscodeService  from "services/wallet-passcode.service";
import AuthService            from "services/auth.service";
import "./Withdraw.scss";

const formatCountdown = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const PasscodeGateModal = ({ show, locked, retryIn, onUnlocked, onNeedsSetup, onCancel }) => {
  const [passcode, setPasscode]     = useState("");
  const [error, setError]           = useState(null);
  const [verifying, setVerifying]   = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [showHelp, setShowHelp]     = useState(false);

  useEffect(() => {
    if (!show) return;

    setPasscode("");
    setError(null);
    setVerifying(false);
    setShowHelp(false);
  }, [show]);

  useEffect(() => {
    if (!show) return;

    setSecondsLeft(locked ? (retryIn || 0) : 0);
  }, [show, locked, retryIn]);

  useEffect(() => {
    if (!show) return undefined;

    const timer = setInterval(() => {
      setSecondsLeft((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [show]);

  const handleComplete = (code) => {
    setVerifying(true);
    setError(null);

    WalletPasscodeService.verify({ passcode: code }).then(
      (data) => onUnlocked(data),
      (err) => {
        const status  = err.response && err.response.status;
        const code_   = err.response?.data?.code;
        const message = err.response?.data?.error;
        const info    = err.response?.data?.wallet_passcode || {};

        setPasscode("");

        if (status === 401) {
          AuthService.logout();
          window.location.replace("/eb/login");
          return;
        }

        if (code_ === "wallet_passcode_not_set") {
          // An admin cleared it since this modal opened — go set a new one.
          onNeedsSetup();
        } else if (code_ === "wallet_passcode_locked") {
          setError(message || "Too many incorrect attempts.");
          setSecondsLeft(info.retry_in || 0);
        } else if (code_ === "wallet_passcode_incorrect") {
          // attempts_remaining is in the response but deliberately not surfaced.
          setError(message || "Incorrect passcode.");
        } else if (status === 429) {
          toast.error(message || "Too many attempts. Please try again in a moment.");
        } else {
          toast.error(message || "Could not verify your passcode");
        }
      }
    ).then(() => {
      setVerifying(false);
    });
  };

  const isLocked = secondsLeft > 0;

  return (
    <Modal className="wallet-passcode-modal" size="lg" show={ show } onHide={ onCancel } backdrop="static" centered>
      <Modal.Header className="p-0">
        <button type="button" className="close close-button position-relative pb-0" onClick={ onCancel }>
          <img src={ CloseBtn } alt="modal-close" className="w-30" />
        </button>
      </Modal.Header>

      <Modal.Body>
        <h4 className="fweight-700">Enter your wallet passcode.</h4>
        <p className="modal-subtitle">
          This page contains your bank account details, so we need your 6-digit passcode before you continue.
        </p>

        <PasscodeInput
          value={ passcode }
          onChange={ setPasscode }
          onComplete={ handleComplete }
          invalid={ !!error }
          disabled={ verifying || isLocked }
          autoFocus={ !isLocked }
        />

        { isLocked
          ? <p className="passcode-countdown">
              Too many incorrect attempts. Please try again in { formatCountdown(secondsLeft) }.
            </p>
          : error && <div className="field-error text-center mt-3">{ error }</div>
        }

        { showHelp
          ? <p className="passcode-help">
              Kindly contact the BookDoc admin at <a href="mailto:care@bookdoc.com">care@bookdoc.com</a> if
              you have any issues with your passcode.
            </p>
          : <button type="button" className="btn forgot-passcode" onClick={ () => setShowHelp(true) }>
              Forgot passcode?
            </button>
        }
      </Modal.Body>

      <Modal.Footer className="d-block">
        <div className="modal-footer-actions">
          <button type="button" className="btn btn-cancel" onClick={ onCancel }>
            Back to My Wallet
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PasscodeGateModal;
